import {uniq} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {AvailableDocument} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

type TemplateOption = {label: string; value: string};
type GroupedTemplateOptions = {label: string | undefined; options: TemplateOption[]};

const toTemplateOption = (template: {id: string; title: string}): TemplateOption => ({
  label: template.title,
  value: template.id,
});

/**
 * Build grouped `FormField type="choice"` options for a single document code from the
 * entity-scoped available-templates response. Templates are grouped by authorization profile
 * (labeled with the profile name), followed by a "without authorization" group. Unlike the
 * service-order-variant reference, no primary template is derived (WRH keeps no default prefill).
 */
export function getGroupedTemplateOptions(
  availableDocuments: AvailableDocument[] | undefined,
  authorizationProfilesById: Record<string, {name: string}>,
  documentCode: string
): GroupedTemplateOptions[] {
  const document = availableDocuments?.find((doc) => doc.documentCode === documentCode);

  if (isNilOrEmpty(document?.templates)) {
    return [];
  }

  const templates = document?.templates ?? [];
  const authTemplates = templates.filter((template) =>
    isNotNilOrEmpty(template.authorizationProfileId)
  );
  const defaultTemplates = templates.filter((template) =>
    isNilOrEmpty(template.authorizationProfileId)
  );

  const authGroups: GroupedTemplateOptions[] = uniq(
    authTemplates.map((template) => template.authorizationProfileId)
  ).map((authorizationProfileId) => ({
    label:
      authorizationProfilesById[authorizationProfileId ?? '']?.name ??
      i18n.t('general.labels.template'),
    options: authTemplates
      .filter((template) => template.authorizationProfileId === authorizationProfileId)
      .map(toTemplateOption),
  }));

  const defaultGroup: GroupedTemplateOptions[] = isNotNilOrEmpty(defaultTemplates)
    ? [
        {
          label: i18n.t('entity.authorizationProfiles.labels.withoutAuthorization'),
          options: defaultTemplates.map(toTemplateOption),
        },
      ]
    : [];

  return [...authGroups, ...defaultGroup];
}
